import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { injectMutation, injectQuery, injectQueryClient } from './query'
import { QueryObserverResult } from '@tanstack/query-core'
import { map, Observable, tap } from 'rxjs'
import {
  MinutesVerboseness,
  MinutesAnonymization,
  MinutesPassive,
  MinutesNameFormat,
  MinutesDateFormat,
  MinutesTimeFormat,
  MinutesLanguage,
  MinutesAttribution,
  AdminMeetingDetails,
  AgendaTranscriptMappingMode,
  MeetingMedium,
  MinutesStyling,
  MinutingAgent,
} from './meetings.service'
import { DownloadsService } from './downloads.service'
import { ListParameterQuery } from './list-query-options'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { AdminListUser, User } from './users.service'
import { generateHeadersWithTimezone } from './utils'
import { ResolvedSettings, ClientSettings, EncryptionKeySource } from './client-settings'

export type EnterpriseOidcProviderPlatform = 'AZURE' | 'OKTA' | 'GOOGLE' | 'OTHER'
export type EnterpriseOidcProviderType = 'AUTH' | 'CALENDAR' | 'AUTH_AND_CALENDAR'
export type GuestOrganization = {
  id: number
  name: string
  domains: string[]
}

export type EnterpriseOidcProvider = {
  platform: EnterpriseOidcProviderPlatform
  type: EnterpriseOidcProviderType
  keycloakHint: string
}

export type TeamClient = {
  id: number
  name: string
  isInternal: boolean
} & ClientSettings

export type Team = {
  id: number
  name: string
  clientId: number
  client: TeamClient
  // What the assistant is actually called in this team's meetings, computed and sanitized
  // server-side (see resolveAssistantDisplayName in the backend): the team's own override,
  // else the client's assistantDisplayName, else the client's name with " Assistant" appended.
  resolvedAssistantDisplayName: string
  canBeSupport: boolean
  shareUrlOptOut: boolean | null
  transcriberInputLanguage: string | null
  transcriberOutputLanguage: string | null
  allowTranscriptDownload: boolean | null
  allowVideoDownload: boolean | null
  allowMinutesDownload: boolean | null
  allowOutlineDownload: boolean | null
  allowManualMeetingCreation: boolean | null
  allowMeetingUpload: boolean | null
  isIntercomEnabled: boolean | null
  isSharingEnabled: boolean | null
  isCalendarSynchronizationEnabled: boolean | null
  allowSignUp: boolean | null
  llmModelOverride: string | null
  encryptionKeySourceOverride: EncryptionKeySource | null
  useVerificationDiscovery: boolean | null
  anonymiseMemberSpeakers: boolean | null
  useTranscriptRefinement: boolean | null
  useAgendaTranscriptMapping: AgendaTranscriptMappingMode | null
  allowBoardPackUploads: boolean | null
  allowAdminMeetingActions: boolean | null
  allowRetentionExtensionRequests: boolean | null
  recordingType: 'VIDEO' | 'AUDIO' | null
  retentionPeriodInDaysForMediaOverride: number | null
  retentionPeriodInDaysForDocumentsOverride: number | null
  retentionPeriodInDaysForTranscriptOverride: number | null
  retentionPeriodInDaysForMeetingOverride: number | null
  maxSingleRetentionExtensionInDaysOverride: number | null
  maxTotalRetentionExtensionInDaysOverride: number | null
  softDeletionPeriodInDays: number | null
  assistantDisplayNameOverride: string | null
  domains: string[]
  microsoftTenantIds: string[]
  enterpriseOidcProviders: EnterpriseOidcProvider[]
  contactEmails: string[]
  teamType: string
  deactivated: boolean
  createdAt: Date
  defaultMeetingMedium: MeetingMedium | null
  sendSoftDeletionWarningEmail: boolean | null
  softDeletionWarningHours: number | null
  sendSoftDeletionConfirmationEmail: boolean | null
  sendHardDeletionConfirmationEmail: boolean | null
  sendAssistantAccessDeniedEmail: boolean | null
  sendUpcomingMeetingReminderEmail: boolean | null
  sendWeeklyDigestEmail: boolean | null
  sendConsentNotification: boolean | null
  joinMeetingInternal: boolean | null
  joinMeetingExternal: boolean | null
  freezeJoinSettings: boolean | null
  useAuthenticatedAssistant: boolean
  useNativeAssistant: boolean
  useSectionHeadings: boolean | null
  usePartialMinutesRegeneration: boolean | null
  allowRestrictedUsers: boolean | null
  allowMeetingEntities: boolean | null
  allowActionItems: boolean | null
  allowLlmSpeechRecovery: boolean | null
  sessionTimeoutInMinutes: number | null
  defaultMeetingProfileId: number | null
  context: string[]
  scimKey: string | null
  // The minutes styling settings are team-level overrides of the client defaults.
  minutesVerboseness: MinutesVerboseness | null
  minutesAnonymization: MinutesAnonymization | null
  minutesPassive: MinutesPassive | null
  minutesNameFormat: MinutesNameFormat | null
  minutesDateFormat: MinutesDateFormat | null
  minutesTimeFormat: MinutesTimeFormat | null
  minutesLanguage: MinutesLanguage | null
  minutesAttribution: MinutesAttribution | null
}

export type TeamAudioMetrics = {
  avgWhisperScore: number | null
  whisperScoreVariance: number | null
  avgRms: number | null
  avgHfRatio: number | null
  avgWhisperScoreLast10: number | null
  avgRmsLast10: number | null
  avgHfRatioLast10: number | null
}

export type TeamDetails = Team & {
  users: User[]
  // What actually applies once the client defaults and this team's overrides are combined.
  resolvedSettings: ResolvedSettings
}

// A new team may be created without picking a client, in which case the backend gives it a
// client of its own named after the team. It also has no resolvedAssistantDisplayName yet —
// that field is computed server-side, and nothing has been saved to compute it from.
export type TeamToBeCreated = Omit<Team, 'id' | 'createdAt' | 'clientId' | 'client' | 'resolvedAssistantDisplayName'> & {
  clientId: number | null
}

export type TeamQueryResult = {
  list: (Team & { userCount: number })[]
  total: number
}

export type TeamUpdateData = {
  team: Team
}

export type TeamFeature =
  'AGENDA_TRANSCRIPT_MAPPING' | 'BOARD_PACK_UPLOADS' | 'PARTIAL_MINUTES_REGENERATION' | 'ACTION_ITEMS' | 'TRANSCRIPT_REFINEMENT' | 'LLM_SPEECH_RECOVERY'

export type SetTeamFeature = {
  teamIds: number[]
  feature: TeamFeature
  enabled: boolean
}

export type TeamCreateData = {
  team: TeamToBeCreated
}

export type UsageEntry = {
  totalMeetingDurationInSeconds: number
  totalMeetingCount: number
}

export type TeamUsage = {
  label: string
  usage: UsageEntry
}[]

// The whole team row, so the view page can report each setting's own value next to the
// client's and the effective one.
export type AdminTeamDetails = Team & {
  // What actually applies once the client defaults and this team's overrides are combined.
  resolvedSettings: ResolvedSettings
} & {
  pastMeetings: AdminMeetingDetails[]
  upcomingMeetings: AdminMeetingDetails[]
  erroredMeetings: AdminMeetingDetails[]
  usage: TeamUsage
  users: AdminListUser[]
  guestOrganization: GuestOrganization
}

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)
  downloadsService = inject(DownloadsService)

  getAllTeams(query: string | null = null, sortField: string | null = null, sortDirection: 'asc' | 'desc' | null = null, clientIds: number[] = []) {
    const params: { query?: string; sortField?: string; sortDirection?: 'asc' | 'desc'; clientIds?: number[] } = {}
    if (query !== null) {
      params.query = query
    }
    if (sortField !== null) {
      params.sortField = sortField
    }
    if (sortDirection !== null) {
      params.sortDirection = sortDirection
    }
    if (clientIds.length > 0) {
      params.clientIds = clientIds
    }
    return this.query({
      queryKey: [...queryKeys.teams.all, query, sortField, sortDirection, clientIds],
      queryFn: () =>
        this.httpClient.get<(Team & { userCount: number } & Partial<TeamAudioMetrics>)[]>('/api/teams', {
          params,
        }),
    }).result$
  }

  getTeams(page: number, pageSize: number, sortField?: string, sortDirection?: 'asc' | 'desc') {
    const params: ListParameterQuery = { page, pageSize }
    if (sortField) {
      params.sortField = sortField
      params.sortDirection = sortDirection
    }

    return this.query({
      queryKey: queryKeys.teams.base,
      queryFn: () => this.httpClient.get<TeamQueryResult>('/api/teams/support', { params }),
    }).result$
  }

  getTeam(id: number, receiveUpdates = false) {
    return this.query({
      queryKey: queryKeys.teams.byId(id),
      queryFn: () => this.httpClient.get<TeamDetails>(`/api/teams/${id}`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  createTeam() {
    return this.mutation({
      mutationFn: (teamCreateDto: TeamCreateData) => this.httpClient.post<Team>('/api/teams', teamCreateDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.base }),
      onError: () => {
        this.toastService.show('TEAMS.CREATE_FAILED')
      },
    })
  }

  updateTeam() {
    return this.mutation({
      mutationFn: (teamUpdateDto: TeamUpdateData) => this.httpClient.patch<Team>(`/api/teams/${teamUpdateDto.team.id}`, teamUpdateDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.base }),
      onError: () => {
        this.toastService.show('TEAMS.EDIT_FAILED')
      },
    })
  }

  refreshMicrosoftTenantIds() {
    return this.mutation({
      mutationFn: (teamId: number) => this.httpClient.post<Team>(`/api/teams/${teamId}/refresh-microsoft-tenant-ids`, {}),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.base }),
      onError: () => {
        this.toastService.show('TEAMS.REFRESH_MICROSOFT_TENANT_IDS_FAILED')
      },
    })
  }

  setTeamFeature() {
    return this.mutation({
      mutationFn: (setTeamFeatureDto: SetTeamFeature) => this.httpClient.patch<void>('/api/teams/features', setTeamFeatureDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.base }),
      onError: () => {
        this.toastService.show('TEAMS.ADMIN.FEATURES.UPDATE_FAILED')
      },
    })
  }

  deleteTeam() {
    return this.mutation({
      mutationFn: (id: number) => this.httpClient.delete<void>(`/api/teams/${id}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.base })
      },
      onError: () => {
        this.toastService.show('TEAMS.ADMIN.DELETE_FAILED')
      },
    })
  }

  activateAllUsers() {
    return this.mutation({
      mutationFn: ({ teamId, notify }: { teamId: number; notify: boolean }) =>
        this.httpClient.post<number>(`/api/teams/${teamId}/activate-all-users`, { notify }),
      onSuccess: (_, { teamId }) => this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.byId(teamId) }),
      onError: () => {
        this.toastService.show('TEAMS.ADMIN.ACTIVATE_ALL_USERS_FAILED')
      },
    })
  }

  deactivateAllUsers() {
    return this.mutation({
      mutationFn: ({ teamId, notify }: { teamId: number; notify: boolean }) =>
        this.httpClient.post<number>(`/api/teams/${teamId}/deactivate-all-users`, { notify }),
      onSuccess: (_, { teamId }) => this.queryClient.invalidateQueries({ queryKey: queryKeys.teams.byId(teamId) }),
      onError: () => {
        this.toastService.show('TEAMS.ADMIN.DEACTIVATE_ALL_USERS_FAILED')
      },
    })
  }

  getAdminViewToTeam(id: number): Observable<QueryObserverResult<AdminTeamDetails>> {
    return this.query({
      queryKey: queryKeys.teams.supportById(id),
      queryFn: () => this.httpClient.get<AdminTeamDetails>(`/api/teams/${id}/support`),
      refetchInterval: 30000,
    }).result$
  }

  downloadUsageReport(team: AdminTeamDetails, fromDate: Date, toDate: Date): Observable<void> {
    const fileName = `usage-report-${team.name}-${fromDate.toISOString()}-${toDate.toISOString()}.csv`
    return this.httpClient
      .get(`/api/teams/${team.id}/usage-report`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
        params: {
          startDate: fromDate.toISOString(),
          endDate: toDate.toISOString(),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'csv')
        }),
        map(() => void 0)
      )
  }
}
